import {
    IconFinalReport,
    IconIncidentManagement,
    IconLiveQm,
    IconReport,
    IconReportHistory,
    IconSettings,
    IconTime,
    IconTv
} from '@sportcast/ui-react';
import React, {createContext, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {logout} from '../../fetch/logout.ts';
import {RootState, useAppDispatch} from '../../store';
import {PermissionModule} from '../../types/enums/permission/PermissionModule';
import {PermissionType} from '../../types/enums/permission/PermissionType';
import {useCanReadAnyReport, useHasPermission, useIsAdminOrQMOrReportResponsible} from '../../util/PermissionUtil';

export type NavigationItem = {
    icon: React.ReactElement,
    title: string,
    selected: boolean,
    href: string
}

type NavigationState = {
    navigationItems: Array<NavigationItem>,
    bottomNavigationItems: Array<NavigationItem>,
}

type NavigationContextType = NavigationState & {
    goHome: () => void
    logout: () => void
}

export const useNavigationContext = () => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const canSeeLiveQm = useHasPermission(PermissionModule.LIVE_QM, PermissionType.READ);
    const canSeeIncidentManagement = useHasPermission(PermissionModule.INCIDENT_MANAGEMENT, PermissionType.READ);
    const canSeeSummary = useHasPermission(PermissionModule.SUMMARY, PermissionType.READ);
    const canSeeReports = useCanReadAnyReport();
    const canSeeReporting = useHasPermission(PermissionModule.REPORTING, PermissionType.READ);
    const unreadIncidents = useSelector((state: RootState) => state.unreadIncidents);
    const canSeeReportsForQMResponsibles = useHasPermission(PermissionModule.REPORT, PermissionType.WRITE);
    const canSeeArchiveReport = useHasPermission(PermissionModule.REPORT_ARCHIVE, PermissionType.READ);
    const canSeeExtensionDeadline = useIsAdminOrQMOrReportResponsible();

    const logoutCallback = useCallback(() => {
        logout(dispatch);
    }, [dispatch]);

    const goHome = useCallback(() => {
        navigate('/');
    }, [navigate]);

    const showHelp = useCallback(() => {
        window.open('/help', '_blank');
    }, []);

    const showContact = useCallback(() => {
        window.open('/contact', '_blank');
    }, []);

    const isSelected = useCallback((path: string) => {
        if (!path && location.pathname === '/') {
            return true;
        }

        return location.pathname.indexOf(path) > -1;
    }, [location]);

    const navigationItems = useMemo(() => {
        let navigationItems: Array<NavigationItem> = [];
        if (canSeeLiveQm) {
            navigationItems.push({
                icon: <IconLiveQm/>,
                title: t('navigation.LIVE_QM'),
                selected: isSelected('/live') || isSelected('/checklist'),
                href: '/live'
            });
        }

        if (canSeeSummary) {
            navigationItems.push({
                icon: <IconTv/>,
                title: t('navigation.SUMMARY'),
                selected: location.pathname.startsWith('/summary'),
                href: '/summary'
            });
        }

        if (canSeeReports) {
            navigationItems = navigationItems.concat([
                {
                    icon: <IconReport/>,
                    title: canSeeReportsForQMResponsibles ? t('navigation.REPORTS') : t('navigation.MY_REPORTS'),
                    selected: isSelected('/reports'),
                    href: '/reports'
                }]);
        }
        if (canSeeArchiveReport) {
            navigationItems = navigationItems.concat([
                {
                    icon: <IconReportHistory/>,
                    title: t('navigation.ARCHIVE_REPORTS'),
                    selected: isSelected('/archive-reports'),
                    href: '/archive-reports'
                }]);

        }

        if (canSeeReporting) {
            navigationItems.push({
                icon: <IconFinalReport/>,
                title: t('navigation.REPORTING'),
                href: '/reporting/staff',
                selected: isSelected('/reporting/')
            });
        }

        if (canSeeIncidentManagement) {
            navigationItems.push({
                icon: (<>
                    <IconIncidentManagement/>
                    {!!unreadIncidents?.unreadIncidents && <span className="incident-badge">{unreadIncidents.unreadIncidents}</span>}
                </>),
                title: t('navigation.INCIDENT_MANAGEMENT'),
                href: '/incident-management',
                selected: isSelected('/incident-management')
            });
        }

        if (canSeeExtensionDeadline) {
            navigationItems.push({
                icon: <IconTime/>,
                title: t('navigation.EXTENSION_DEADLINE'),
                href: '/extension-deadline',
                selected: isSelected('/extension-deadline')
            });
        }

        return navigationItems;
    }, [canSeeReportsForQMResponsibles, canSeeIncidentManagement, canSeeLiveQm, canSeeReporting, canSeeArchiveReport, canSeeReports, canSeeSummary, canSeeExtensionDeadline, location.pathname, isSelected, t, unreadIncidents.unreadIncidents]);

    const bottomNavigationItems = useMemo(() => {
        return [{
            icon: <IconSettings/>,
            title: t('navigation.SETTINGS'),
            href: '/settings/edit-profile',
            selected: !!location.pathname && isSelected('/settings/')
        }];
    }, [isSelected, t, location.pathname]);

    return useMemo(() => ({ // NOSONAR
        navigationItems,
        bottomNavigationItems,
        goHome,
        logout: logoutCallback,
        showHelp,
        showContact
    }), [navigationItems, bottomNavigationItems, goHome, logoutCallback, showHelp, showContact]);
};

const NavigationContext = createContext<NavigationContextType>({} as NavigationContextType);

export default NavigationContext;
